import React, { useEffect, useMemo } from 'react';
import {
  FormControlLabel,
  RadioGroup,
  FormControl,
  FormLabel,
  Checkbox,
  TextField,
  Radio,
  FormHelperText
} from '@mui/material';
import useStyles from '../useStyles';
import PropTypes from 'prop-types';
import ErrorIcon from '@mui/icons-material/Error';
import HtmlEditor from '../../../NewUI/Components/Utilities/HtmlEditor';

function Step3({
  answers,
  setAnswers,
  activeError,
  requiredRefs,
  linkOutEnabled,
  privacyOverLimit,
  activeErrorMessage,
  jobId,
  setActiveError,
  setActiveErrorMessage,
  job_url_prop
}) {
  const classes = useStyles();

  const isAdDetailsOverLimit = useMemo(() => {
    if (!answers.ad_details) return false;
    return answers.ad_details.length > 15000;
  }, [answers.ad_details]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className={classes.stepContainer}>
      <FormControl
        ref={requiredRefs.title}
        fullWidth={true}
        className={`${classes.formControl} ${classes.medium}`}
      >
        <FormLabel required={true} className={classes.label}>
          Job Title
        </FormLabel>
        <TextField
          error={activeError === requiredRefs.title}
          className={classes.textField}
          variant="outlined"
          value={answers.title}
          onChange={(e) =>
            e.target.value.length >= 0 && setAnswers({ ...answers, title: e.target.value })
          }
          InputProps={{
            endAdornment: activeError === requiredRefs.title && (
              <div className={classes.error}>
                <ErrorIcon color="error" />
                <span>This field is required</span>
              </div>
            )
          }}
        />
      </FormControl>
      <FormControl
        fullWidth={true}
        className={`${classes.formControl} ${classes.editor}`}
        error={activeError === requiredRefs.ad_details || isAdDetailsOverLimit}
      >
        <FormLabel focused={false} className={classes.label}>
          Ad Details
          <span className={classes.asterisk}> *</span>
          {activeError === requiredRefs.ad_details && (
            <div className={classes.error}>
              <ErrorIcon color="error" />
              <span>This field is required</span>
            </div>
          )}
        </FormLabel>
        <div>
          Supported formatting styles: Headings (only H2), Italics, Bold, Underline, Bullet
          Points/Numbered List
        </div>
        <div>
          NOTE: If your job description contains unsupported formatting styles, they will be removed
          from the Ad Details section.
        </div>
        <HtmlEditor
          body={answers.ad_details}
          onChange={(e) => setAnswers({ ...answers, ad_details: e })}
        />
        {isAdDetailsOverLimit && (
          <FormHelperText error>
            Character limit exceeded. Please reduce the content to comply with Seek Ad&apos;s
            requirements.
          </FormHelperText>
        )}
      </FormControl>

      <h4>Candidate Application Options</h4>
      {linkOutEnabled && (
        <div>
          <RadioGroup
            value={answers.link_out}
            onChange={() => setAnswers({ ...answers, link_out: true })}
          >
            <FormControlLabel
              label={'Apply through :Recruit Talent Landing Page'}
              checked={answers.link_out === true}
              control={<Radio />}
            />
          </RadioGroup>
          <FormControl
            fullWidth={true}
            style={{ margin: '0.5rem 0 1.5rem 0' }}
            className={`${classes.formControl} ${classes.large}`}
          >
            <TextField
              className={classes.textField}
              style={{ marginLeft: '1rem' }}
              variant="outlined"
              disabled={answers.link_out === false}
              value={answers.application_url || ''}
              onChange={(e) =>
                e.target.value.length >= 0 &&
                setAnswers({ ...answers, application_url: e.target.value })
              }
            />
          </FormControl>
        </div>
      )}
      {linkOutEnabled && (
        <RadioGroup
          value={answers.link_out}
          onChange={() => setAnswers({ ...answers, link_out: false })}
        >
          <FormControlLabel
            ref={requiredRefs.link_out}
            label={'Apply through SEEK'}
            checked={answers.link_out === false}
            disabled={answers.link_out && answers.advertisement_id}
            control={<Radio />}
          />
          {activeError === requiredRefs.link_out &&
            activeErrorMessage &&
            activeErrorMessage.length >= 1 && (
              <span>
                {activeErrorMessage.map((error, i) => (
                  <div
                    style={{ display: 'flex', marginTop: '0.5rem' }}
                    className={classes.errorMessage}
                    key={i}
                  >
                    <ErrorIcon color="error" />
                    <div className="seekValidationErrorMsg">{error}.</div>
                  </div>
                ))}
                <div
                  className={classes.reviewQuestionText}
                  onClick={() => {
                    window
                      .open(`${window.location.origin}/admin/jobs/${jobId}/fields`, '_blank')
                      .focus();
                    setActiveError(null);
                    setActiveErrorMessage(null);
                  }}
                >
                  Review questions
                </div>
              </span>
            )}
        </RadioGroup>
      )}
      {!answers.link_out && linkOutEnabled && (
        <RadioGroup value={answers.posting_questionnaire} className={classes.secondaryRadioGroup}>
          <FormControlLabel
            label={'Include application questionnaire'}
            checked={answers.posting_questionnaire}
            disabled={answers.advertisement_id}
            className={classes.secondaryRadio}
            control={
              <Radio
                onChange={() =>
                  setAnswers({ ...answers, posting_questionnaire: true, link_out: false })
                }
              />
            }
          />
          <FormControlLabel
            label={'Do not include application questionnaire'}
            checked={!answers.posting_questionnaire}
            disabled={answers.advertisement_id}
            className={classes.secondaryRadio}
            control={
              <Radio
                className={classes.secondaryRadio}
                onChange={() =>
                  setAnswers({ ...answers, posting_questionnaire: false, link_out: false })
                }
              />
            }
          />
          <div className={classes.optionHint}>
            <strong>Please note: </strong>
            {answers.posting_questionnaire && (
              <span>
                All questions will be mandatory and cannot be changed once this option has been
                selected and the ad has been published. Please click{' '}
                <a href={job_url_prop} target="_blank" rel="noreferrer">
                  HERE
                </a>{' '}
                to alter questions if required before posting.
              </span>
            )}
            {!answers.posting_questionnaire && (
              <span>
                Candidates will receive an email to fill out the missing questions after applying.
              </span>
            )}
          </div>
        </RadioGroup>
      )}
      {!linkOutEnabled && (
        <RadioGroup value={answers.posting_questionnaire}>
          <FormControlLabel
            label={'Include application questionnaire'}
            checked={answers.posting_questionnaire}
            disabled={answers.advertisement_id}
            className={classes.secondaryRadio}
            control={
              <Radio
                onChange={() =>
                  setAnswers({ ...answers, posting_questionnaire: true, link_out: false })
                }
              />
            }
          />
          <FormControlLabel
            label={'Do not include application questionnaire'}
            checked={!answers.posting_questionnaire}
            disabled={answers.advertisement_id}
            className={classes.secondaryRadio}
            control={
              <Radio
                className={classes.secondaryRadio}
                onChange={() =>
                  setAnswers({ ...answers, posting_questionnaire: false, link_out: false })
                }
              />
            }
          />
          <div className={classes.optionHint}>
            <strong>Please note: </strong>
            {answers.posting_questionnaire && (
              <span>
                All questions will be mandatory and cannot be changed once this option has been
                selected and the ad has been published. Please click{' '}
                <a href={job_url_prop} target="_blank" rel="noreferrer">
                  HERE
                </a>{' '}
                to alter questions if required before posting.
              </span>
            )}
            {!answers.posting_questionnaire && (
              <span>
                Candidates will receive an email to fill out the missing questions after applying.
              </span>
            )}
          </div>
        </RadioGroup>
      )}
      <FormControlLabel
        style={{ marginTop: '2rem' }}
        label={'Include a privacy policy agreement'}
        control={
          <Checkbox
            checked={answers.include_privacy}
            disabled={privacyOverLimit || answers.advertisement_id}
            onChange={() => setAnswers({ ...answers, include_privacy: !answers.include_privacy })}
          />
        }
      />
      {privacyOverLimit && (
        <div>
          Privacy Policy agreement is unable to be included because it is is over Seek’s 250
          character limit. Please check entity settings or contact Support
        </div>
      )}
    </div>
  );
}

Step3.propTypes = {
  answers: PropTypes.object.isRequired,
  setAnswers: PropTypes.func.isRequired,
  requiredRefs: PropTypes.object.isRequired,
  activeError: PropTypes.ref,
  linkOutEnabled: PropTypes.bool.isRequired,
  privacyOverLimit: PropTypes.bool,
  activeErrorMessage: PropTypes.array,
  jobId: PropTypes.string,
  setActiveError: PropTypes.func,
  setActiveErrorMessage: PropTypes.func,
  job_url_prop: PropTypes.string
};

export default Step3;
